import { useRandomGitaVerse } from "../../lib/gita";
import { normalizeShloka, normalizeText } from "./BhagavadGita";

/**
 * Compact Bhagavad Gītā card. Same data source as the landing variant —
 * one random verse per render — trimmed for the Library Featured tab:
 *
 *   - chapter:verse heading
 *   - Devanāgarī text (smaller font, single column)
 *   - the first translation (italic, no author selector)
 *   - "Another verse" — refetches in place
 *
 * Always renders a visible card frame so the Featured grid keeps its
 * second column populated even while the verse is loading or if the
 * gita endpoint hiccups.
 */

export function BhagavadGitaCompact() {
  const { data: verse, isLoading, isError, refetch, isFetching } = useRandomGitaVerse();
  const translation = verse?.translations?.[0];

  const showLoading = isLoading || (!verse && !isError);

  return (
    <div className="border border-[var(--color-rule)] bg-[var(--color-bg)] p-6 sm:p-7 flex flex-col min-h-[260px]">
      <header className="flex items-baseline justify-between gap-3 mb-3">
        <p className="page-eyebrow">Bhagavad Gītā</p>
        {verse && (
          <p className="font-grotesk text-[0.62rem] tracking-[0.18em] uppercase text-[var(--color-ink-mute)]">
            {verse.chapter}.{verse.verse}
          </p>
        )}
      </header>

      {showLoading && (
        <div className="flex flex-col items-center justify-center flex-1 py-6 text-center">
          <span className="text-3xl font-serif text-[var(--color-brand-muted)] animate-pulse">ॐ</span>
          <p className="mt-3 uppercase tracking-[0.2em] text-[0.65rem] text-[var(--color-ink-mute)]">
            Loading verse…
          </p>
        </div>
      )}

      {isError && !verse && (
        <div className="flex flex-col items-center justify-center flex-1 py-6 text-center">
          <p className="font-display-italic text-[0.95rem] text-[var(--color-ink-mute)] mb-3">
            Couldn't reach the verse store just now.
          </p>
          <button
            type="button"
            onClick={() => refetch()}
            className="font-grotesk text-[0.62rem] tracking-[0.16em] uppercase text-[var(--color-violet)] hover:text-[var(--color-ink)] transition-colors"
          >
            Try again
          </button>
        </div>
      )}

      {verse && (
        <>
          <p className="font-serif text-xl sm:text-2xl leading-snug text-[var(--color-ink)] mb-5 whitespace-pre-wrap">
            {normalizeShloka(verse.text)}
          </p>

          {translation && (
            <p className="font-display-italic text-[1rem] sm:text-[1.05rem] leading-relaxed text-[var(--color-ink-soft)] border-l-2 border-[var(--color-brand-muted)] pl-4 mb-4">
              {normalizeText(translation.description)}
            </p>
          )}

          <div className="mt-auto pt-4 border-t border-[var(--color-rule)] flex items-center justify-between gap-3">
            {translation ? (
              <p className="font-grotesk text-[0.62rem] tracking-[0.16em] uppercase text-[var(--color-ink-mute)]">
                — {translation.author_name}
              </p>
            ) : (
              <span />
            )}
            <button
              type="button"
              onClick={() => refetch()}
              disabled={isFetching}
              className="font-grotesk text-[0.62rem] tracking-[0.16em] uppercase text-[var(--color-violet)] hover:text-[var(--color-ink)] transition-colors disabled:opacity-60"
            >
              {isFetching ? "…" : "Another verse"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
